import React from "react";
import { Card, CardContent, CardHeader, Typography, Box, Button, Grid, FormControlLabel, Checkbox } from "@mui/material";

interface LegalTermsCardProps {
  companyName: string;
  companyNif: string;
  companyAddressType: string;
  companyMail: string;
  companyPhone: string;
  handleAccept: () => void;
  error: boolean;
  apiError: boolean;
  isAccepted: boolean;
  setIsAccepted: (value: boolean) => void;
  previousStep: () => void;
}

const LegalTermsCardEng: React.FC<LegalTermsCardProps> = ({
  companyName,
  companyNif,
  companyAddressType,
  companyMail,
  companyPhone,
  handleAccept,
  error,
  apiError,
  isAccepted,
  setIsAccepted,
  previousStep
}) => {
  return (
    <Card sx={{ p: 2 }}>
      <CardHeader
        title={
          <Typography variant="h1" align="center">
            Legal Terms
          </Typography>
        }
      />
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "5px",
          }}
        >
          {/* Legal terms container */}
          <Box
            sx={{
              border: "1px solid black",
              borderRadius: "5px",
              padding: "20px",
              maxHeight: "53vh",
              overflowY: "scroll",
              backgroundColor: "white",
              marginBottom: "20px",
            }}
          >
            <Typography
              variant="body1"
              sx={{ marginBottom: "10px", textAlign: "center" }}
            >
              <strong>KYC FORM</strong>
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: "10px" }}>
              <strong>
                The information in this form is requested in compliance with
                anti-money laundering regulations (Law 10/2010 and RD 304/2014)
              </strong>
            </Typography>
            <Typography
              variant="body1"
              sx={{ marginBottom: "10px", textAlign: "center" }}
            >
              <strong>DATA PROTECTION POLICY</strong>
            </Typography>
            <Typography
              variant="body2"
              sx={{ marginBottom: "10px", textAlign: "center" }}
            >
              <strong>Basic information about data protection</strong>
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: "10px" }}>
              <strong>Data Controller:</strong> {companyName}
              <br />
              <strong>Purpose:</strong> Identification and verification of the
              identity of individuals wishing to establish business relationships
              or engage in transactions with us, to determine whether they act
              on their own behalf or for others, and the intended purpose and
              nature of the business relationship, as well as the source of the
              funds.
              <br />
              <strong>Legal basis:</strong> Compliance with a legal obligation.
              <br />
              <strong>Recipients:</strong> Data processors: SEPBLAC, CPBCIM.
              <br />
              <strong>Rights:</strong> Access, rectify, and delete data, as well
              as other rights, as explained in the following information:
              <br />
              In accordance with Article 32 bis.3 of Law 10/2010, of April 28, on
              Prevention of Money Laundering and Terrorism Financing (PBCFT), and
              Articles 13 of Regulation (EU) 2016/679 and 11 of Organic Law 3/2018,
              we provide you with the necessary information on personal data
              processing for PBCFT purposes:
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: "10px" }}>
              <strong>Who is responsible for data processing?</strong>
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: "10px" }}>
              {companyName}, s.l. with CIF {companyNif}
              <br />
              {companyAddressType}
              <br />
              {companyMail}
              <br />
              Tel. {companyPhone}
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: "10px" }}>
              <strong>For what purpose do we process your personal data?</strong>
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: "10px" }}>
              We will process the personal data provided in this form and the
              attached documentation, as well as data related to transactions
              with {companyName}, in order to identify individuals wishing to
              establish business relationships or engage in transactions with us,
              verify their identity, determine whether they act on their own
              behalf or for others, and the intended purpose and nature of the
              business relationship, as well as the source of the funds.
            </Typography>

            <Typography variant="body1" sx={{ marginBottom: "10px" }}>
              <strong>
                Is providing your personal data mandatory?
              </strong>
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: "10px" }}>
              Providing the personal data indicated in this form is mandatory
              in order for this establishment to comply with its legal
              obligations. Otherwise, {companyName} will not be able to establish
              or maintain business relationships or carry out transactions with
              you.
            </Typography>

            <Typography variant="body1" sx={{ marginBottom: "10px" }}>
              <strong>
                What are our legal obligations regarding the processing of
                personal data for PBCFT purposes?
              </strong>
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: "10px" }}>
              In compliance with the mentioned regulations, entities subject to
              this regulation must fulfill a series of legal obligations
              regarding the processing of personal data for PBCFT purposes. In
              general terms, these obligations include informing individuals about
              such processing; not using the data for other purposes without the
              consent of the individual, unless the processing is necessary for
              the ordinary management of the business relationship; and carrying
              out a data protection impact assessment to adopt enhanced technical
              and organizational measures to ensure the integrity, confidentiality,
              and availability of the data.
            </Typography>

            <Typography variant="body1" sx={{ marginBottom: "10px" }}>
              <strong>For how long will we keep your data?</strong>
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: "10px" }}>
              Personal data will be kept as long as the business relationship
              lasts and/or the transaction is carried out, and for a period of
              ten years from the termination of the business relationship or the
              execution of the occasional transaction, in compliance with Law
              10/2010, of April 28, on PBCFT.
            </Typography>

            <Typography variant="body1" sx={{ marginBottom: "10px" }}>
              <strong>
                What is the legal basis for processing your data?
              </strong>
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: "10px" }}>
              The legal basis for processing your personal data for the
              purposes mentioned above is compliance with a legal obligation
              applicable to this establishment. Specifically, as an obliged
              subject, it must comply with the identification and due diligence
              obligations established by Law 10/2010, of April 28, on PBCFT and
              Royal Decree 304/2014, of May 5, for the development of that Law.
            </Typography>

            <Typography variant="body1" sx={{ marginBottom: "10px" }}>
              <strong>To whom will your data be communicated?</strong>
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: "10px" }}>
              In addition to the data processors who provide us with services,
              in compliance with the aforementioned regulations, we may also
              disclose your data to SEPBLAC, the Commission for the Prevention of
              Money Laundering and Monetary Offenses (CPBCIM). Judicial and/or
              police authorities, government agencies, law enforcement, and
              regulatory bodies when required or mandated by law.
            </Typography>

            <Typography variant="body1" sx={{ marginBottom: "10px" }}>
              <strong>
                What are your rights when providing your data?
              </strong>
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: "10px" }}>
              Any person has the right to confirm whether or not {companyName}
              is processing personal data related to them. Interested persons also
              have the right to access their personal data, request the correction
              of inaccurate data, or, if applicable, request its deletion when
              the data is no longer necessary for the purposes for which it was
              collected.<br />
              In the cases provided for in the regulations, you have the right to data portability. In certain circumstances, you may request the restriction of the processing of your data, in which case, except for its retention, we will only process it for the formulation, exercise, or defense of claims or in other cases provided for by applicable legislation.
              <br />
              In certain circumstances and for reasons related to your particular situation, you may object to the processing of your data. We will stop processing your data unless there are compelling legitimate reasons or for the formulation, exercise, or defense of potential claims.
            </Typography>

            <Typography variant="body2" sx={{ marginBottom: "10px" }}>
              You can exercise your rights at any of our workplaces or submit a request, attaching a document proving your identity and a description of your request, by postal mail to {companyName}, {companyAddressType}, by email to {companyMail}, or calling to {companyPhone}.
            </Typography>

            <Typography variant="body2">
              I declare that I have understood the information provided and understand the processing that will be carried out on my personal data.
            </Typography>
            {error && (
              <Typography
                variant="body2"
                sx={{ color: "red", marginBottom: "20px" }}
              >
                You must accept the terms to continue.
              </Typography>
            )}
            {apiError && (
              <Typography
                variant="body2"
                sx={{ color: "red", marginBottom: "20px" }}
              >
                Error in communication with the server.
              </Typography>
            )}
            {/* Checkbox to accept the terms */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAccepted}
                  onChange={(e) => setIsAccepted(e.target.checked)}
                  color="success"
                />
              }
              label={
                <Typography variant="body2">
                  I have read and accept the Privacy Policy.
                </Typography>
              }
            />

            {/* Button to continue */}
            <Grid container spacing={2} justifyContent="space-between">
              <Grid item xs={12} lg={6}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={previousStep}
                  sx={{
                    mt: 2,
                    backgroundColor: "#FFF",
                    display: {
                      xs: "none", // Hide on small screens (mobile)
                      sm: "none", // Hide on tablets
                      md: "block", // Show on medium and large screens
                    },
                  }}
                >
                  Back
                </Button>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Button
                  variant="contained"
                  onClick={handleAccept}
                  fullWidth
                  sx={{
                    mt: 2,
                    backgroundColor: "black",
                    color: "white",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: "#333",
                    },
                  }}
                >
                  Accept and continue
                </Button>
              </Grid>
            </Grid>



          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LegalTermsCardEng;
